import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { ICHING_LIMITS } from '../../core/iching-limits.const';
import { IChingMethodStep } from '../../core/iching.models';
import { IChingDraft } from '../hd-iching-draft';
import { injectIChingTranslate } from '../iching-consult-translate';

type QuestionKind = IChingDraft['questionKind'];

/**
 * Fase 1 van de raadpleging: de vraag (of de situatie). Tips klappen open op
 * verzoek, voorbeeldchips vullen de vraag in, en een rustzin sluit af.
 */
@Component({
  selector: 'hd-iching-question-step',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ESegmentedToggleComponent],
  templateUrl: './hd-iching-question-step.component.html',
  styles: `
    :host { display: block; }
    .question { display: flex; flex-direction: column; gap: 10px; }
    .text {
      width: 100%; min-height: 72px; padding: 8px 10px; box-sizing: border-box; resize: vertical;
      border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.18)); border-radius: var(--radius-md, 6px);
      font: inherit; font-size: 14px; line-height: 1.45; background: var(--hd-iching-input-bg, transparent); color: inherit;
    }
    .hint { margin: 0; font-size: 13px; opacity: 0.85; }
    .tools { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    .tips-toggle {
      padding: 2px 10px; border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.18));
      border-radius: 999px; background: transparent; color: inherit; font: inherit; font-size: 12.5px; cursor: pointer;
    }
    .tips { margin: 0; padding-left: 18px; font-size: 13px; line-height: 1.5; }
    .tips li { margin-bottom: 4px; }
    .examples-label { font-size: 12px; opacity: 0.7; }
    .examples { display: flex; flex-wrap: wrap; gap: 6px; }
    .chip {
      padding: 3px 10px; border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.15));
      border-radius: 999px; background: var(--hd-iching-card-bg, rgba(0, 0, 0, 0.035));
      color: inherit; font: inherit; font-size: 12.5px; text-align: left; cursor: pointer;
    }
    .chip:hover { background: rgba(0, 0, 0, 0.07); }
    .calm { margin: 4px 0 0; font-size: 13px; font-style: italic; opacity: 0.8; }
  `,
})
export class HdIChingQuestionStepComponent {
  readonly question = input.required<string>();
  readonly questionKind = input.required<QuestionKind>();
  readonly step = input.required<IChingMethodStep>();

  readonly questionChange = output<string>();
  readonly questionKindChange = output<QuestionKind>();

  protected readonly t = injectIChingTranslate();
  protected readonly tipsOpen = signal(false);
  protected readonly maxLength = ICHING_LIMITS.question;

  protected readonly kindOptions = computed<ESegmentedToggleOption<QuestionKind>[]>(() => [
    { value: 'question', label: this.t('humanDesign.iching.question.kindQuestion') },
    { value: 'situation', label: this.t('humanDesign.iching.question.kindSituation') },
  ]);

  protected useExample(example: string): void {
    if (this.questionKind() !== 'question') {
      this.questionKindChange.emit('question');
    }
    this.questionChange.emit(example);
  }
}
